import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

const triggerCsvDownload = (blobData) => {
  const url = window.URL.createObjectURL(new Blob([blobData]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `brain360_assessments_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Admin dashboard data + action state machine.
 * Handles fetching, selection, deletion (single/selected/all), and CSV export.
 */
export const useAdminDashboard = ({ apiBase, session, navigate }) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/admin/assessments`);
      setAssessments(response.data.assessments);
    } catch (error) {
      logger.error("Error fetching assessments:", error);
      toast.error("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (session?.user_type !== "admin") {
      toast.error("Access denied. Admin access required.");
      navigate("/");
      return;
    }
    fetchAssessments();
  }, [session, navigate, fetchAssessments]);

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(assessments.map(a => a.id)) : new Set());
  };

  const handleSelectOne = (id, checked) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  };

  const handleDeleteClick = (target) => {
    setDeleteTarget(target);
    setShowDeleteDialog(true);
  };

  const deleteAll = async () => {
    const response = await axios.delete(`${apiBase}/admin/assessments/all`);
    toast.success(`Deleted ${response.data.assessments_deleted} assessments`);
    setSelectedIds(new Set());
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    await Promise.all(ids.map(id => axios.delete(`${apiBase}/admin/assessment/${id}`)));
    toast.success(`Deleted ${ids.length} assessments`);
    setSelectedIds(new Set());
  };

  const deleteSingle = async (id) => {
    await axios.delete(`${apiBase}/admin/assessment/${id}`);
    toast.success("Assessment deleted successfully");
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget === 'all') await deleteAll();
      else if (deleteTarget === 'selected') await deleteSelected();
      else await deleteSingle(deleteTarget);
      fetchAssessments();
    } catch (error) {
      logger.error("Error deleting assessment(s):", error);
      toast.error("Failed to delete assessment(s)");
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${apiBase}/admin/export/csv`, { responseType: 'blob' });
      triggerCsvDownload(response.data);
      toast.success("CSV exported successfully");
    } catch (error) {
      logger.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const handleViewReport = (assessmentId) => {
    window.open(`/report/${assessmentId}`, '_blank');
  };

  const allSelected = assessments.length > 0 && selectedIds.size === assessments.length;
  const someSelected = selectedIds.size > 0;

  return {
    assessments,
    loading,
    selectedIds,
    allSelected,
    someSelected,
    showDeleteDialog,
    setShowDeleteDialog,
    deleteTarget,
    fetchAssessments,
    handleSelectAll,
    handleSelectOne,
    handleDeleteClick,
    handleDelete,
    handleExportCSV,
    handleViewReport,
  };
};
